import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import { updateUserSettings, getUserSettings } from '../../services/database';
import { Save, RotateCcw, Zap, Clock, Settings as SettingsIcon } from 'lucide-react';

const TypingSettings = ({ onClose }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    defaultTypingSpeed: 80,
    randomVariation: 30,
    enableBackspaces: true,
    theme: 'light'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) return;

    setIsLoading(true);
    const unsubscribe = getUserSettings(user, (userSettings) => {
      setSettings(userSettings);
      setIsLoading(false);
    });

    return unsubscribe;
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    setMessage('');

    try {
      await updateUserSettings(user, settings);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to save settings. Please try again.');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      defaultTypingSpeed: 80,
      randomVariation: 30,
      enableBackspaces: true,
      theme: 'light'
    });
  };

  const speedPresets = [
    { label: 'Slow', value: 120, description: 'Slow and steady' },
    { label: 'Normal', value: 80, description: 'Human-like speed' },
    { label: 'Fast', value: 50, description: 'Quick typing' },
    { label: 'Instant', value: 20, description: 'Very fast' }
  ];

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-300">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Typing Settings
          </h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ×
          </button>
        )}
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.includes('success') 
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Default Typing Speed */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Zap className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Default Typing Speed
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Speed: {settings.defaultTypingSpeed}ms per character
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {settings.defaultTypingSpeed <= 30 ? 'Very Fast' : 
                 settings.defaultTypingSpeed <= 60 ? 'Fast' : 
                 settings.defaultTypingSpeed <= 100 ? 'Normal' : 'Slow'}
              </span>
            </div>
            
            <input
              type="range"
              min="10"
              max="200"
              value={settings.defaultTypingSpeed}
              onChange={(e) => setSettings({...settings, defaultTypingSpeed: parseInt(e.target.value)})}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {speedPresets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setSettings({...settings, defaultTypingSpeed: preset.value})}
                  className={`p-2 text-xs rounded-lg border transition-colors ${
                    settings.defaultTypingSpeed === preset.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-medium">{preset.label}</div>
                  <div className="text-xs opacity-75">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Random Variation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Typing Variation
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Random variation: ±{settings.randomVariation}ms
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {settings.randomVariation <= 10 ? 'Very Consistent' : 
                 settings.randomVariation <= 30 ? 'Consistent' : 
                 settings.randomVariation <= 50 ? 'Natural' : 'Very Natural'}
              </span>
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              value={settings.randomVariation}
              onChange={(e) => setSettings({...settings, randomVariation: parseInt(e.target.value)})}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Higher values make typing appear more human-like with natural timing variations
            </p>
          </div>
        </div>

        {/* Advanced Options */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Advanced Options
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Backspaces
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Occasionally simulate typing errors and corrections
                </p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableBackspaces}
                onChange={(e) => setSettings({...settings, enableBackspaces: e.target.checked})}
                className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Theme
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choose your preferred interface theme
                </p>
              </div>
              <select
                value={settings.theme}
                onChange={(e) => setSettings({...settings, theme: e.target.value})}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TypingSettings;
